import { FC, useMemo, useState } from 'react';
import { differenceInSeconds } from 'date-fns';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import {
  VsTransferEvent,
  useCancelAssociateTransferRequest,
  useGetAllAssociatesForTransfer,
  useGetHeroConversationById,
} from '@vs-online-only-app/state';
import { CountdownTimer, UserAvatar } from '@vs-online-only-app/ui';
import { RootTransferCard } from './OutgoingTransferCard';

type Props = {
  event: VsTransferEvent;
  onCancel: () => void;
  slideIn?: boolean;
};

const MAX_DISPLAYED_AVATARS = 6;

export const PendingTransferCard: FC<Props> = ({
  event,
  onCancel,
  slideIn,
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const { data: conversation } = useGetHeroConversationById(event.conversation);
  const { data: allAssociatesForTransfer } = useGetAllAssociatesForTransfer();
  const cancelTransferMutation = useCancelAssociateTransferRequest();

  const requestedAssociates = useMemo(() => {
    return allAssociatesForTransfer?.filter((associate) =>
      event.requested.includes(associate.id),
    );
  }, [allAssociatesForTransfer, event]);

  if (!conversation || !requestedAssociates || !isVisible) {
    return null;
  }

  return (
    <RootTransferCard elevation={1} $slideIn={slideIn}>
      <CountdownTimer
        onTimeout={() => setIsVisible(false)}
        remainingDurationSeconds={differenceInSeconds(
          new Date(event.expiration),
          new Date(),
          {
            roundingMethod: 'ceil',
          },
        )}
        initialDurationSeconds={120}
        isMuted
      />
      <Stack gap={2}>
        <Stack alignItems="flex-start">
          <Typography fontWeight="bold" variant="body2" align="left">
            Waiting for a response...
          </Typography>
          <Typography variant="body2" color="textSecondary" align="left">
            We requested your transfer for{' '}
            {conversation.conversationStarter.firstName}.
          </Typography>
        </Stack>
        <Stack direction="row" gap={0.5} flexWrap="nowrap">
          {requestedAssociates
            .slice(0, MAX_DISPLAYED_AVATARS - 1)
            .map((associate) => (
              <Tooltip
                title={`${associate.firstName} ${associate.lastName}`}
                arrow
                key={associate.id}
                placement="top"
              >
                <div>
                  <UserAvatar imageSrc={associate?.image} />
                </div>
              </Tooltip>
            ))}
          {requestedAssociates.length - MAX_DISPLAYED_AVATARS > 0 ? (
            <UserAvatar
              fallbackCharacter={`+${
                requestedAssociates.length - MAX_DISPLAYED_AVATARS
              }`}
              color="secondary"
            />
          ) : null}
        </Stack>
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            cancelTransferMutation.mutateAsync(event.id);
            onCancel();
            setIsVisible(false);
          }}
        >
          Cancel transfer
        </Button>
      </Stack>
    </RootTransferCard>
  );
};
